import { toQueryString } from '../../framework/apis/apiGenerator';
import { env } from '../../framework/utils/EnvUtils';
import { TypeUtils } from '../../framework/utils/UtilityTypes';
import { DownloadAttachedFileQuery } from '../hooks/queries/attachedFile';
import { EngeneFileObject } from '../models/EngeneFileObject';
import { FileKind } from '../models/gen/fileKind';

const indexedFileNamePattern = /^.+_\d+\.\w+$/;
const indexedFileNameExtractPattern = /^.+_(\d+)\.\w+$/;

type ContentTypeInfo = {
  contentTypes: Array<string>;
  errMessage?: string;
};
const isContentTypeInfo = (
  contentTypeInfo: unknown
): contentTypeInfo is ContentTypeInfo =>
  typeof contentTypeInfo === 'object' &&
  TypeUtils.isNonNil(contentTypeInfo) &&
  'contentTypes' in contentTypeInfo;
const isValidContentTypeInfo = (
  contentTypeInfo: unknown
): contentTypeInfo is ContentTypeInfo =>
  isContentTypeInfo(contentTypeInfo) &&
  Array.isArray(contentTypeInfo.contentTypes);

/**
 *
 * @param fileNames -
 * @returns
 */
const getNextIndex = (fileNames: Array<string | undefined>) => {
  const matched = fileNames.filter((fileName) =>
    indexedFileNamePattern.test(fileName || '')
  );
  const unmatchedNum = fileNames.length - matched.length;

  return (
    Math.max(
      0,
      ...matched.map((fileName) => {
        const extracted =
          indexedFileNameExtractPattern.exec(fileName || '') || [];
        return TypeUtils.isNil(extracted)
          ? 0
          : (parseInt(extracted[1]) as number);
      })
    ) +
    unmatchedNum +
    1
  );
};

/**
 *
 * @param files -
 * @returns
 */
const getNextIndexFrom = (files: Array<EngeneFileObject>) => {
  return getNextIndex(files.map((file) => file.newFileName || file.fileName));
};

/**
 *
 * @param files -
 * @param facilitySettings -
 * @param fileKind -
 * @returns
 */
const validate = (
  files: EngeneFileObject[] = [],
  facilitySettings: Record<string, unknown> = {},
  fileKind: FileKind
) => {
  if (fileKind !== FileKind.ReportDoc && fileKind !== FileKind.Receipt) {
    return 'ファイル種別が不正です';
  }
  const attachedFileMaxNum = facilitySettings.ATTACHED_FILE_MAX_NUM;
  if (TypeUtils.isNonNilNumber(attachedFileMaxNum)) {
    if (files.length > attachedFileMaxNum) {
      return '添付最大数を超えています';
    }
  } else return '施設パラメータが不正です．ログインし直してください';

  const attachedFileMaxTotalMB = facilitySettings.ATTACHED_FILE_MAX_TOTAL_MB;
  if (TypeUtils.isNonNilNumber(attachedFileMaxTotalMB)) {
    if (
      files.reduce((prev, cur) => prev + (cur.fileSize || 0) / 1048576, 0) >
      attachedFileMaxTotalMB
    ) {
      return '添付可能総ファイルサイズを超えています';
    }
  } else return '施設パラメータが不正です．ログインし直してください';

  const attachedFileContentTypeInfo =
    fileKind === FileKind.ReportDoc
      ? facilitySettings.REPORT_DOC_ATTACHED_FILE_CONTENT_TYPE_INFO
      : facilitySettings.EXPENSE_ATTACHED_FILE_CONTENT_TYPE_INFO;
  if (isValidContentTypeInfo(attachedFileContentTypeInfo)) {
    for (const file of files) {
      if (TypeUtils.isNilNumber(file.fileId)) {
        const contentType = file.file?.type;
        if (TypeUtils.isNilString(contentType)) {
          return `${file.fileName} のファイル種別が不明です`;
        }
        console.log(contentType, attachedFileContentTypeInfo.contentTypes);
        if (!attachedFileContentTypeInfo.contentTypes.includes(contentType)) {
          return TypeUtils.isNilString(attachedFileContentTypeInfo.errMessage)
            ? `${file.fileName} (${contentType}) はアップロードできません`
            : `${attachedFileContentTypeInfo.errMessage} (${file.fileName})`;
        }
      }
    }
  } else return '施設パラメータが不正です．ログインし直してください';

  return null;
};

/**
 *
 * @param query -
 * @returns
 */
const toFileDownloadUrl = (query: DownloadAttachedFileQuery) =>
  `${env.BASE_API_ENDPOINT}/attached-file/download${toQueryString(query)}`;

/**
 *
 * @param query -
 * @returns
 */
const toFileGetUrl = (query: DownloadAttachedFileQuery) =>
  `${env.BASE_API_ENDPOINT}/attached-file${toQueryString(query)}`;

export const FileUtils = {
  getNextIndex,
  getNextIndexFrom,
  toFileDownloadUrl,
  toFileGetUrl,
  validate,
};
